import type {ReactElement} from 'react';
import React, {useEffect, useRef, useCallback} from 'react';
import {Timeline} from '@Components/poster-editor/components/timeline/timeline';
import {updateTimelineBottomBarHeight, persistBottomBarHeight} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {
  BOTTOM_BAR_MAX_HEIGHT,
  BOTTOM_BAR_MIN_HEIGHT,
  POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {PosterEditorErrorBoundary} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editor-error-boundary';
import {PlayButton} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/play-button/play-button';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import styles from './poster-editor-web-bottom-bar.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {EDITOR_SIDEBAR_WIDTH} from '@Components/poster-editor/library/poster-editor-library';
import {TimelineZoomOptions} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-zoom-options/timeline-zoom-options';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';

export function PosterEditorWebBottomBar(): ReactElement<any> {
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });
  const dispatch = useAppDispatch();
  const isSidebarOpen = useAppSelector((state) => {
    return state.posterEditor.isSidebarOpen;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const isPickingModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isPickingModeEnabled;
  });

  // Resize state refs
  const isResizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleResizeMove = (e: MouseEvent): void => {
    if (!isResizingRef.current) {
      return;
    }
    const editorWithoutNav = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.EDITOR_WITHOUT_NAV];
    const editorWithoutNavHeight = editorWithoutNav?.height() ?? 0;
    const minWhiteboardHeight = 330;
    const dynamicMaxHeight = Math.min(BOTTOM_BAR_MAX_HEIGHT, editorWithoutNavHeight - minWhiteboardHeight);

    const deltaY = startYRef.current - e.clientY;
    const newHeight = Math.min(dynamicMaxHeight, Math.max(BOTTOM_BAR_MIN_HEIGHT, startHeightRef.current + deltaY));
    dispatch(updateTimelineBottomBarHeight(newHeight));
  };

  const handleResizeEnd = useCallback((): void => {
    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleResizeMove);
    window.removeEventListener('mouseup', handleResizeEnd);
    if (bottomBarRef.current) {
      persistBottomBarHeight(bottomBarRef.current.clientHeight);
    }
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      isResizingRef.current = true;
      startYRef.current = e.clientY;
      startHeightRef.current = bottomBarHeight ?? BOTTOM_BAR_MIN_HEIGHT;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
    },
    [bottomBarHeight, handleResizeMove, handleResizeEnd]
  );

  useEffect((): void => {
    if (bottomBarRef.current && window.posterEditor) {
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_WEB_BOTTOM_BAR] = $(bottomBarRef.current);
    }
  }, []);

  const getCloseIcon = (): ReactElement => {
    return (
      <ClickableDiv
        onClick={(): void => {
          window.posterEditor?.whiteboard?.updateIsTimelineMode(false);
        }}
        className={`_position-absolute cursor-pointer flex-center ${styles.closeIcon}`}
      >
        <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} />
      </ClickableDiv>
    );
  };

  const getHeightForTimelineContent = (): string => {
    return `${bottomBarHeight}px`;
  };

  const getWidthForWebBottomBar = (): string => {
    const paddingOnSides = 12;
    if (isSidebarSmall && !isSidebarOpen) {
      const collapsedContextualMenuWidth = 22;
      return `calc(100% - ${paddingOnSides * 2}px - ${collapsedContextualMenuWidth}px)`;
    }
    return `calc(100% - ${paddingOnSides * 3}px - ${EDITOR_SIDEBAR_WIDTH}px)`;
  };

  return (
    <PosterEditorErrorBoundary>
      <div
        ref={bottomBarRef}
        style={{
          height: getHeightForTimelineContent(),
          width: getWidthForWebBottomBar(),
        }}
        id={POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID}
        className={`${styles.container} ${isPickingModeEnabled ? styles.pickingMode : ''}`}
      >
        <div className={styles.resizeHandle} onMouseDown={handleResizeStart} role="separator" aria-orientation="horizontal" aria-label="Resize timeline" />
        {getCloseIcon()}
        <div className={`flex-v-row flex-justify-end shadow-static-elevation-2 radius-12 border-s-standard _overflow-hidden _full-height ${styles.innerContainer}`}>
          <div className={styles.timelineContainer}>
            <Timeline />
          </div>
          <div className={`flex flex-row-justify-between flex-items-center _full-width spacing-p-2 ${styles.bottomContent}`}>
            <PlayButton />
            <TimelineZoomOptions />
          </div>
        </div>
      </div>
    </PosterEditorErrorBoundary>
  );
}
